import {useMemo} from 'react';

import {ProcedureEdgeNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {ConditionGroup, isPersistedCondition} from 'studies/typed/shared/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunGraphQL as ProcedureRunNode} from './__generated/GetStudyDesignFromProcedureRunGraphQL';
import {
  getConditionReplicateEdges,
  getMethodExecutionInstanceInfoForPlacements,
} from './getStudyDesignFromProcedureRun';
import {
  buildPmeiToNodeIdMap,
  executionEdgeSpecsFromConditionGraph,
  FlowchartEdgeSpec,
  methodLinksFromProcedureEdges,
  edgeSpecsFromMethodLinks,
} from './ProcedureRunDesignFlowchartLayoutUtils';

interface BaseEdgeSpecsInputs {
  isFlowchartProcedureRunDesignEnabled: boolean;
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
}

/**
 * Execution-time inputs: a promoted `ProcedureRun` has a per-run condition graph, the source of truth
 * for the actual execution structure (split/pool can diverge from the template). `procedureEdges`
 * remains optional so draft (newly added) conditions can still render template-method fan-out edges.
 */
interface ExecutionInputs {
  procedureRun: ProcedureRunNode;
  activeMethodId: string | null;
  procedureEdges?: Array<ProcedureEdgeNode>;
}

/**
 * Planning-time inputs: pre-promotion there's no per-run condition graph, so the template procedure
 * graph (`procedureEdges`, filtered down to method links) is fanned out across each
 * condition/replicate row instead.
 */
interface NonExecutionInputs {
  procedureRun?: never;
  activeMethodId?: never;
  procedureEdges: Array<ProcedureEdgeNode>;
}

export type UseProcedureRunDesignFlowchartEdgeSpecsProps = BaseEdgeSpecsInputs &
  (ExecutionInputs | NonExecutionInputs);

// Stable default so the execution path (which omits `procedureEdges`) keeps a referentially stable
// `useMemo` dependency instead of allocating a fresh array each render.
const EMPTY_PROCEDURE_EDGES: Array<ProcedureEdgeNode> = [];

/**
 * Derives the design flowchart's edge specs for both the run and plan tabs. When a promoted
 * `procedureRun` is supplied, edges come from its runtime condition graph (resolving PMEIs to grid
 * nodes, marking edges past the active unit-op layer `downstream`). Otherwise the template method
 * graph is fanned out per condition/replicate row for planning. Returns no edges when the flowchart UI
 * is disabled, since only it consumes them.
 */
export function useProcedureRunDesignFlowchartEdgeSpecs({
  isFlowchartProcedureRunDesignEnabled,
  conditions,
  methods,
  procedureRun,
  activeMethodId,
  procedureEdges = EMPTY_PROCEDURE_EDGES,
}: UseProcedureRunDesignFlowchartEdgeSpecsProps): Array<FlowchartEdgeSpec> {
  return useMemo(() => {
    if (!isFlowchartProcedureRunDesignEnabled) {
      return [];
    }
    if (procedureRun) {
      const methodIndexById = new Map(methods.map((method, index) => [method.id, index]));
      const activeMethodIndex = activeMethodId == null ? -1 : (methodIndexById.get(activeMethodId) ?? -1);
      const executionEdgeSpecs = executionEdgeSpecsFromConditionGraph(
        getConditionReplicateEdges(procedureRun),
        buildPmeiToNodeIdMap(getMethodExecutionInstanceInfoForPlacements(procedureRun), conditions),
        {methods, activeMethodId}
      );
      const draftConditionEdgeSpecs = edgeSpecsFromMethodLinks({
        conditions,
        methods,
        links: methodLinksFromProcedureEdges(procedureEdges),
        shouldIncludeCondition: (condition) => !isPersistedCondition(condition),
        getVariantForLink: (link) => {
          if (activeMethodIndex < 0) {
            return 'default';
          }
          const sourceMethodIndex = methodIndexById.get(link.sourceMethodId);
          return sourceMethodIndex != null && sourceMethodIndex >= activeMethodIndex
            ? 'downstream'
            : 'default';
        },
      });
      return [...executionEdgeSpecs, ...draftConditionEdgeSpecs];
    }
    return edgeSpecsFromMethodLinks({
      conditions,
      methods,
      links: methodLinksFromProcedureEdges(procedureEdges),
    });
  }, [
    isFlowchartProcedureRunDesignEnabled,
    conditions,
    methods,
    procedureRun,
    activeMethodId,
    procedureEdges,
  ]);
}
